/**
 * Phase 2.7 event moments + push alerts. Mounted once at app level.
 * Moments (full-screen alert, optional sound, shake, THALIA line, feed post, share card):
 *   Lucky Strike (Viper bonus roll), Extinction survival, round wins, season titles.
 * Push alerts (browser notifications, only after the player allows them):
 *   Flash Crash, Extinction incoming (<5 min), outbid, unclaimed test FRAME decays in <12h.
 * Polls logs every 12s from the public RPC; read-only, no transactions.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { phase2, FACTION_NAMES, fmtLeft } from './phase2Read';
import { playSound, sendNotification } from '../utils/notifications';
import './eventMoments.css';

type Kind = 'lucky' | 'extinction' | 'win' | 'season';
interface Moment { kind: Kind; title: string; line: string; detail: string }

const THALIA: Record<Kind, string[]> = {
  lucky: ['The dice liked you. I noted it.', 'Lucky Strike. Fortune is just math you did not see coming.'],
  extinction: ['The clock was cut and you are still standing. Interesting.', 'Extinction came. You did not go extinct.'],
  win: ['Last one holding the line. The round is yours.', 'No tomorrow, and yet here you are, winning today.'],
  season: ['A season ends. The board remembers who held it.', 'Titles are earned in points, not promises.'],
};
const pick = (a: string[]) => a[Math.floor(Math.random() * a.length)];
const MUTE_KEY = 'nt3d.moments.muted';
const GRI_ABI = ['function getRoundInfo() view returns (uint256 roundId,uint256 pot,uint256 totalKeysSold,uint256 deadline,address lastBuyer,uint256 lastBuyTime,uint256 currentPrice,uint256 timeRemaining)'];

/** Feed post: LiveEventFeed (and anything else) can listen for this window event. */
export const MOMENT_EVENT = 'nt3d:moment';

function shareSvg(m: Moment): string {
  const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="630"><rect width="1200" height="630" fill="#07060d"/>`
    + `<text x="60" y="120" fill="#ff2a8a" font-family="monospace" font-size="40">NO TOMORROW 3D · testnet</text>`
    + `<text x="60" y="260" fill="#fff" font-family="monospace" font-size="72">${esc(m.title)}</text>`
    + `<text x="60" y="340" fill="#46def8" font-family="monospace" font-size="32">${esc(m.detail)}</text>`
    + `<text x="60" y="460" fill="#bbb" font-family="monospace" font-size="28">THALIA: "${esc(m.line)}"</text>`
    + `<text x="60" y="580" fill="#666" font-family="monospace" font-size="22">test FRAME only · game points, no real value</text></svg>`;
}

export default function EventMoments({ account, faction }: { account: string | null; faction: number }) {
  const [m, setM] = useState<Moment | null>(null);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === '1');
  const fromBlock = useRef<number>(0);
  const alerted = useRef<Set<string>>(new Set());
  const wasLeader = useRef(false);

  const fire = useCallback((mo: Moment) => {
    setM(mo);
    if (!muted) playSound(mo.kind === 'win' || mo.kind === 'season' ? 'jackpot' : 'roundEnd');
    document.body.classList.add('nt3d-shake');
    setTimeout(() => document.body.classList.remove('nt3d-shake'), 700);
    window.dispatchEvent(new CustomEvent(MOMENT_EVENT, { detail: mo }));
  }, [muted]);

  const push = (key: string, title: string, body: string) => {
    if (alerted.current.has(key)) return;
    alerted.current.add(key);
    try { if (typeof Notification !== 'undefined' && Notification.permission === 'granted') sendNotification(title, { body, tag: key }); } catch { /* ignore */ }
  };

  useEffect(() => {
    let dead = false;
    const c = phase2();
    const provider = c.runner!.provider!;
    const me = account?.toLowerCase();
    const tick = async () => {
      try {
        const head = await provider.getBlockNumber();
        if (!fromBlock.current) fromBlock.current = head - 5;
        const from = fromBlock.current + 1;
        if (from <= head) {
          const logs = await provider.getLogs({ address: await c.getAddress(), fromBlock: from, toBlock: Math.min(head, from + 500) });
          fromBlock.current = Math.min(head, from + 500);
          for (const lg of logs) {
            let p: ethers.LogDescription | null = null;
            try { p = c.interface.parseLog(lg); } catch { continue; }
            if (!p || dead) continue;
            const a = p.args;
            if (p.name === 'FactionBonusKeys' && Number(a.faction) === 4 && me && a.player.toLowerCase() === me)
              fire({ kind: 'lucky', title: 'LUCKY STRIKE', line: pick(THALIA.lucky), detail: `+${a.bonusKeys} bonus keys on round ${a.roundId}` });
            else if (p.name === 'ExtinctionEvent') {
              push(`ext-hit-${a.roundId}-${a.nextAt}`, 'Extinction event', `The clock just dropped ${a.dropPercent}%.`);
              if (me && faction && account) {
                const held = await c.playerFactionKeys(a.roundId, account).catch(() => 0n);
                if (!dead && BigInt(held) > 0n) fire({ kind: 'extinction', title: 'YOU SURVIVED EXTINCTION', line: pick(THALIA.extinction), detail: `Clock cut ${a.dropPercent}% in round ${a.roundId}` });
              }
            } else if (p.name === 'FlashCrash')
              push(`flash-${a.roundId}-${a.count}`, 'Flash Crash', `Key price crashed in round ${a.roundId}. Ends in ${fmtLeft(Number(a.until) - Date.now() / 1000)}.`);
            else if (p.name === 'RoundEnded' && me && a.winner.toLowerCase() === me)
              fire({ kind: 'win', title: 'ROUND WON', line: pick(THALIA.win), detail: `You won round ${a.roundId} (test FRAME)` });
            else if (p.name === 'SeasonFinalized') {
              const top = [a.first, a.second, a.third].map((x: string) => x.toLowerCase());
              const place = me ? top.indexOf(me) : -1;
              if (place >= 0) fire({ kind: 'season', title: `SEASON ${a.season} · #${place + 1}`, line: pick(THALIA.season), detail: `You placed #${place + 1} in season ${a.season}` });
              else if (faction && Number(a.winningFaction) === faction) fire({ kind: 'season', title: `${FACTION_NAMES[faction].toUpperCase()} TAKE SEASON ${a.season}`, line: pick(THALIA.season), detail: 'Your faction won the season' });
            }
          }
        }
        if (me) {
          const g = new ethers.Contract(await c.getAddress(), GRI_ABI, provider);
          const ri = await g.getRoundInfo();
          const rid = Number(ri.roundId);
          const leader = ri.lastBuyer.toLowerCase() === me;
          if (wasLeader.current && !leader) push(`outbid-${rid}-${ri.lastBuyTime}`, 'Outbid', `Someone took the lead in round ${rid}. ${fmtLeft(Number(ri.timeRemaining))} left.`);
          wasLeader.current = leader;
          const ev = await c.roundChaosEvents(rid).catch(() => null);
          const now = Date.now() / 1000;
          if (ev && Number(ev.nextExtinctionAt) && Number(ev.nextExtinctionAt) - now < 300 && Number(ev.nextExtinctionAt) > now)
            push(`ext-soon-${rid}-${ev.nextExtinctionAt}`, 'Extinction incoming', `An Extinction event can cut the clock in ${fmtLeft(Number(ev.nextExtinctionAt) - now)}.`);
          const dp = await c.decayPreview(account).catch(() => null);
          if (dp && dp[3] > 0n && Number(dp[2])) {
            const stage = Number(dp[1]);
            const at = stage === 0 ? Number(dp[2]) + 72 * 3600 : stage === 1 ? Number(dp[2]) + 7 * 86400 : 0;
            if (at && at - now < 12 * 3600 && at > now) push(`decay-${stage}-${dp[2]}`, 'Unclaimed test FRAME decays soon', `Decay starts in ${fmtLeft(at - now)}. Claim or buy to reset.`);
          }
        }
      } catch { /* RPC hiccup; try next tick */ }
    };
    tick();
    const t = setInterval(tick, 12000);
    return () => { dead = true; clearInterval(t); };
  }, [account, faction, fire]);

  useEffect(() => { if (!m) return; const t = setTimeout(() => setM(null), 9000); return () => clearTimeout(t); }, [m]);

  const toggleMute = () => { const v = !muted; setMuted(v); localStorage.setItem(MUTE_KEY, v ? '1' : '0'); };
  const share = () => {
    if (!m) return;
    const url = URL.createObjectURL(new Blob([shareSvg(m)], { type: 'image/svg+xml' }));
    const a = document.createElement('a'); a.href = url; a.download = `nt3d-${m.kind}.svg`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <>
      <button className="nt3d-moment-mute" onClick={toggleMute} aria-label={muted ? 'Unmute event sounds' : 'Mute event sounds'}>{muted ? '🔇' : '🔊'}</button>
      {m && (
        <div className={`nt3d-moment nt3d-moment-${m.kind}`} role="alertdialog" aria-live="assertive" onClick={() => setM(null)}>
          <div className="nt3d-moment-card" onClick={e => e.stopPropagation()}>
            <div className="nt3d-moment-title">{m.title}</div>
            <div className="nt3d-moment-detail">{m.detail}</div>
            <div className="nt3d-moment-line">THALIA: “{m.line}”</div>
            <div className="nt3d-moment-actions">
              <button onClick={share}>Save share card</button>
              <button onClick={() => setM(null)}>Close</button>
            </div>
            <div className="nt3d-moment-foot">test FRAME only</div>
          </div>
        </div>
      )}
    </>
  );
}
